<template>
  <div class="app-date-select">
    <input
      type="text"
      :class="['form-control date-control', { 'is-invalid': isTouched && !isValid_, 'form-control-sm': small }]"
      @focus="disabled || (calendarEnabled = true)"
      @blur="onUnFocusClose"
      @keyup.enter="apply"
      @change="onChange"
      :value="dateFormatted"
      tabindex="0"
      ref="input"
      :disabled="disabled"
    />
    <div class="app-calendar" v-if="calendarEnabled" ref="calendar">
      <div class="top-bar">
        <div class="month">{{ firstDayOfActiveMonth.format('MMMM YY') }}</div>
        <div class="controls ml-auto">
          <i class="ion ion-md-arrow-up cursor-pointer mr-2" @click="plusMonth" @mousedown.prevent></i>
          <i class="ion ion-md-arrow-down cursor-pointer" @click="subMonth" @mousedown.prevent></i>
        </div>
      </div>
      <app-calendar-table
        @select="select"
        :first-day-of-active-month="firstDayOfActiveMonth"
        :active-date="date_"
        :marks-map="marksMap"
        :marks-format="marksFormat"
        :is-date-selectable="isDateInValidRange"
      ></app-calendar-table>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import AppCalendarTable from '@/components/shared/AppCalendarTable'
import { localeDateFormat } from '@/utils/Helpers'

export default {
  name: 'AppDateSelect',
  components: {
    AppCalendarTable
  },
  props: {
    /**
     * Minimum available date
     * Expect date.js object
     */
    minDate: {
      type: Object,
      default: () => dayjs()
    },
    maxDate: {
      type: Object
    },
    date: {
      type: Object,
      default: null
    },
    viewFormat: {
      type: String,
      default: ''
    },
    isValid: {
      type: Boolean,
      default: true
    },
    small: {
      type: Boolean,
      default: false
    },
    marksMap: {
      type: Map,
      default: () => {
        return new Map()
      }
    },
    marksFormat: {
      type: String,
      default: 'YYYY-MM-DD'
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    firstDayOfActiveMonth: dayjs().date(1),
    calendarEnabled: false,
    date_: null
  }),
  computed: {
    dateFormatted() {
      if (this.date_) {
        const format = this.viewFormat === '' ? localeDateFormat() : this.viewFormat
        return this.date_.format(format)
      }
      return ''
    },
    isValid_() {
      return this.isValid && this.date_ && this.date_.isValid()
    },
    isTouched() {
      return this.date_
    }
  },
  methods: {
    onChange(e) {
      if (e.target.value) {
        this.date_ = dayjs(e.target.value)
      }
    },
    apply() {
      if (this.isValid_) {
        this.$emit('change', this.date_)
      }
    },
    onUnFocusClose() {
      this.calendarEnabled = false
      this.apply()
    },
    plusMonth() {
      this.firstDayOfActiveMonth = this.firstDayOfActiveMonth.add(1, 'month')
    },
    subMonth() {
      this.firstDayOfActiveMonth = this.firstDayOfActiveMonth.subtract(1, 'month')
    },
    isDateInValidRange(cellDay) {
      const isValidForMinimum = cellDay.date.isSameOrAfter(this.minDate, 'date')
      const isValidForMaximum = !this.maxDate || cellDay.date.isSameOrBefore(this.maxDate, 'date')
      return isValidForMinimum && isValidForMaximum
    },
    select(cellDay) {
      if (this.isDateInValidRange(cellDay)) {
        this.date_ = cellDay.date
        this.$refs.input.blur()
      }
    }
  },
  watch: {
    date: {
      immediate: true,
      handler(date) {
        if (date !== null && !dayjs.isDayjs(date)) {
          throw new Error('Date prop should be null or Date.js object')
        }

        if (date && !date.isSame(this.date_, 'date')) {
          this.date_ = date
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
.top-bar {
  display: flex;
  justify-items: center;
  padding: 0 21px 0 7px;

  & .month {
    font-weight: bold;
  }
}

.app-calendar {
  min-width: 230px;
  position: absolute;
  padding: 10px;
  background: #fff;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.4);
  z-index: 1;
}

.date-control {
  padding-left: 10px;
  padding-right: 10px;
}
</style>
